import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useRegister } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import {
  getProfiles,
  getWorkers,
  putProfile,
  putProfileImage,
} from '@/api/adapter/onboarding';
import {
  completeWorkerRegistration,
  pendingRegistration,
} from '@/services/workerRegistration';
import { weeklyMinutes } from '@/lib/auth-validation';
import PasswordAuthForm, {
  type PasswordFormValues,
} from '../components/PasswordAuthForm';
import {
  readSignupDraft,
  saveSignupDraft,
  clearSignupDraft,
} from './signupDraft';
import SignupComplete from '../components/SignupComplete';
import PersonalInformationForm from './components/PersonalInformationForm';
import AboutYourselfForm, {
  type QualificationsValues,
} from './components/AboutYourselfForm';
import AlmostThereForm from './components/AlmostThereForm';
import { DocumentModal } from '@/components/ui/DocumentModal';
import { TERMS_AND_CONDITIONS } from '../constants/legalText';
import { Button } from '@/components/ui/Button';
import type { WorkerProfile, WorkerRegistration } from '@/types/session';

type Step =
  | 'credentials'
  | 'personal-info'
  | 'about-yourself'
  | 'almost-there'
  | 'success';
interface Draft {
  profile: WorkerProfile;
  image: File | null;
  roleIds: number[];
  skillIds: number[];
  hours: number;
}
const emptyProfile: WorkerProfile = {
  name: '',
  contactEmail: null,
  githubUsername: null,
  portfolioUrl: null,
  biography: null,
  background: null,
  proficiency: null,
  location: null,
  languages: [],
};

export default function DeveloperRegisterPage() {
  const navigate = useNavigate();
  const { workerRole, workerPitch } = useDaoConfig();
  const session = useAuthStore((state) => state.session);
  const principalId = session?.principalId;
  const accountId = session?.accountId;
  const displayName = session?.displayName;
  const username = useAuthStore((state) => state.username);
  const authStatus = useAuthStore((state) => state.status);
  const register = useRegister();
  const cached = useRef(readSignupDraft());
  const credentials = useRef<PasswordFormValues | null>(null);
  const finishing = useRef(false);
  const finishingOwner = useRef<string | null>(null);
  const previousPrincipal = useRef(principalId);
  const [step, setStep] = useState<Step>(
    session ? 'personal-info' : 'credentials'
  );
  const [draft, setDraft] = useState<Draft>({
    profile:
      cached.current?.principalId === (principalId ?? null)
        ? cached.current.profile
        : emptyProfile,
    image: null,
    roleIds:
      cached.current?.principalId === (principalId ?? null)
        ? cached.current.roleIds
        : [],
    skillIds:
      cached.current?.principalId === (principalId ?? null)
        ? cached.current.skillIds
        : [],
    hours:
      cached.current?.principalId === (principalId ?? null)
        ? cached.current.hours
        : 40,
  });
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const controller = useRef<AbortController | null>(null);
  useEffect(() => () => controller.current?.abort(), []);

  useEffect(() => {
    if (
      finishing.current &&
      ((principalId ?? null) === finishingOwner.current ||
        (finishingOwner.current === null &&
          username === credentials.current?.username.toLowerCase()))
    ) {
      setLoadedFor(principalId ?? null);
      previousPrincipal.current = principalId;
      return;
    }
    if (previousPrincipal.current !== principalId) {
      credentials.current = null;
      if (!principalId) {
        cached.current = null;
        clearSignupDraft();
        setDraft({
          profile: emptyProfile,
          image: null,
          roleIds: [],
          skillIds: [],
          hours: 40,
        });
      }
    }
    previousPrincipal.current = principalId;
    let active = true;
    controller.current?.abort();
    setLoadedFor(null);
    setError('');
    setBusy(false);
    setPending(false);
    setStep(principalId ? 'personal-info' : 'credentials');
    if (!principalId || !accountId || !displayName) return;
    void Promise.all([getProfiles(), getWorkers()])
      .then(([profiles, workers]) => {
        if (!active) return;
        const current = useAuthStore.getState();
        if (current.session?.principalId === principalId)
          current.setSession(current.session, 'developer');
        current.setProfiles(profiles);
        const worker = workers.find((item) => item.account === accountId);
        const saved = pendingRegistration(principalId);
        const local = readSignupDraft();
        const restored = local?.principalId === principalId ? local : null;
        cached.current = restored;
        setDraft({
          profile: restored?.profile ??
            profiles.worker ?? { ...emptyProfile, name: displayName },
          image: null,
          roleIds:
            saved?.payload.qualifications.roleIds ??
            restored?.roleIds ??
            worker?.qualifications.roleIds ??
            [],
          skillIds:
            saved?.payload.qualifications.skillIds ??
            restored?.skillIds ??
            worker?.qualifications.skillIds ??
            [],
          hours:
            (saved?.payload.calendar.defaultWeeklyMinutes ??
              (restored ? restored.hours * 60 : undefined) ??
              worker?.calendar.definition.defaultWeeklyMinutes ??
              2400) / 60,
        });
        setPending(!!saved);
        setStep(
          saved
            ? 'almost-there'
            : worker && profiles.worker
              ? 'success'
              : 'personal-info'
        );
        setLoadedFor(principalId);
      })
      .catch((reason: unknown) => {
        if (active)
          setError(
            reason instanceof Error ? reason.message : 'Cannot load onboarding.'
          );
      });
    return () => {
      active = false;
    };
  }, [principalId, accountId, displayName, username, loadAttempt]);

  useEffect(() => {
    if (
      authStatus === 'loading' ||
      step === 'success' ||
      (session && loadedFor !== session.principalId)
    )
      return;
    saveSignupDraft({
      ...draft,
      principalId: principalId ?? null,
      username:
        credentials.current?.username ??
        username ??
        cached.current?.username ??
        '',
      profile: {
        ...draft.profile,
        contactEmail: draft.profile.contactEmail ?? null,
      },
      imageSelected: !!draft.image || !!cached.current?.imageSelected,
    });
  }, [draft, principalId, username, authStatus, loadedFor, session, step]);

  const savePersonal = (values: {
    name: string;
    contactEmail: string;
    githubUsername: string;
    portfolioUrl: string;
    image: File | null;
  }) => {
    const profile = {
      ...draft.profile,
      name: values.name,
      contactEmail: values.contactEmail,
      githubUsername: values.githubUsername || null,
      portfolioUrl: values.portfolioUrl || null,
    };
    setDraft((previous) => ({ ...previous, profile, image: values.image }));
    setError('');
    setStep('about-yourself');
  };
  const saveQualifications = (values: QualificationsValues) => {
    const profile = {
      ...draft.profile,
      biography: values.bio,
      background: values.background,
      proficiency: values.proficiency,
      roleExperiences: values.roleIds.map((roleId) => ({
        roleId,
        proficiency: values.proficiency,
      })),
    };
    setDraft((previous) => ({
      ...previous,
      profile,
      roleIds: values.roleIds,
      skillIds: values.skillIds,
    }));
    setError('');
    setStep('almost-there');
  };
  const finish = async (values: {
    location: string;
    languages: string[];
    availableHoursPerWeek?: number;
  }) => {
    if (busy) return;
    let activeSession = session;
    setBusy(true);
    finishing.current = true;
    finishingOwner.current = session?.principalId ?? null;
    setError('');
    setStatus('Saving your profile…');
    controller.current?.abort();
    const operationController = new AbortController();
    controller.current = operationController;
    try {
      const profile = {
        ...draft.profile,
        location: values.location,
        languages: values.languages,
      };
      const hours = values.availableHoursPerWeek ?? draft.hours;
      setDraft((previous) => ({ ...previous, profile, hours }));
      if (!activeSession) {
        if (!credentials.current)
          throw new Error(
            'Re-enter your password in the account step; your profile draft is kept.'
          );
        setStatus('Creating your account…');
        try {
          activeSession = await register.mutateAsync({
            ...credentials.current,
            name: profile.name,
            role: 'developer',
          });
          finishingOwner.current = activeSession.principalId;
          credentials.current = null;
          saveSignupDraft({
            ...draft,
            profile: { ...profile, contactEmail: profile.contactEmail ?? null },
            hours,
            principalId: activeSession.principalId,
            username: useAuthStore.getState().username ?? '',
            imageSelected: !!draft.image,
          });
        } finally {
          register.reset();
        }
      }
      if (
        operationController.signal.aborted ||
        useAuthStore.getState().session?.principalId !==
          activeSession.principalId
      )
        return;
      const assertOwner = () => {
        if (
          operationController.signal.aborted ||
          useAuthStore.getState().session?.principalId !==
            activeSession?.principalId
        )
          throw new Error(
            'Your account changed. Log in to resume registration.'
          );
      };
      const saved = pendingRegistration(activeSession.principalId);
      let payload: WorkerRegistration;
      if (saved) {
        payload = saved.payload;
      } else {
        useAuthStore
          .getState()
          .setProfiles(await putProfile('worker', profile));
        assertOwner();
        if (draft.image) {
          setStatus('Saving your profile picture…');
          await putProfileImage('worker', draft.image);
          assertOwner();
          cached.current = null;
          setDraft((previous) => ({ ...previous, image: null }));
        }
        payload = {
          displayName: profile.name,
          qualifications: { roleIds: draft.roleIds, skillIds: draft.skillIds },
          calendar: {
            defaultWeeklyMinutes: weeklyMinutes(
              values.availableHoursPerWeek ?? draft.hours
            ),
            overrides: [],
          },
        };
        setDraft((previous) => ({
          ...previous,
          profile,
          hours: values.availableHoursPerWeek ?? previous.hours,
        }));
      }
      assertOwner();
      setPending(true);
      const complete = await completeWorkerRegistration(
        activeSession.principalId,
        payload,
        setStatus,
        operationController.signal
      );
      if (complete && !operationController.signal.aborted) {
        setPending(false);
        clearSignupDraft();
        cached.current = null;
        setStep('success');
      }
    } catch (reason) {
      if (!operationController.signal.aborted) {
        setError(
          reason instanceof Error
            ? reason.message
            : 'Worker registration failed.'
        );
        setStatus('');
        try {
          setPending(
            !!activeSession && !!pendingRegistration(activeSession.principalId)
          );
        } catch {
          setPending(true);
        }
      }
    } finally {
      finishing.current = false;
      if (!operationController.signal.aborted) setBusy(false);
    }
  };

  if (authStatus === 'loading') return <p role="status">Checking session…</p>;
  if (step === 'credentials' && !session)
    return (
      <div className="onboardingContent">
        <div className="loginBtn max-w-[calc(100%_-_2rem)] flex-wrap justify-end">
          <span>Already have an account?</span>
          <Button
            variant="secondary"
            onClick={() => navigate('/login/developer')}
          >
            Login
          </Button>
        </div>
        <div className="title">
          <h1>Create an account</h1>
          <p>Choose your username and password</p>
        </div>
        <div className="w-full">
          <p className="mb-2 text-sm text-[--text-dark-secondary]">User type</p>
          <div className="flex gap-3 rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-2] p-3">
            <i
              className="ri-checkbox-circle-fill text-[22px] leading-[22px] text-[--state-brand-active]"
              aria-hidden="true"
            />
            <div className="flex-1 text-sm">
              <p className="text-[--text-dark-primary]">{workerRole}</p>
              <p className="mt-1.5 text-[--text-dark-secondary]">
                {workerPitch}
              </p>
            </div>
            <Link
              to="/register"
              aria-label="Change account type"
              className="text-[--text-dark-secondary]"
            >
              <i className="ri-close-line text-xl" aria-hidden="true" />
            </Link>
          </div>
        </div>
        <PasswordAuthForm
          mode="register"
          role="developer"
          onOpenTerms={() => setTerms(true)}
          initialValues={
            credentials.current
              ? { ...credentials.current, name: draft.profile.name }
              : {
                  username: cached.current?.username ?? '',
                  name: draft.profile.name,
                  password: '',
                }
          }
          submitText="Continue"
          collectName={false}
          onSubmit={async (values) => {
            credentials.current = values;
            setDraft((previous) => ({
              ...previous,
              profile: {
                ...previous.profile,
                name: values.name ?? previous.profile.name,
              },
            }));
            setStep('personal-info');
          }}
        />
        <div className="w-full space-y-2">
          <Button
            variant="secondary"
            disabled
            className="w-full"
            title="Wallet signup is not supported by the current backend"
          >
            <i className="ri-wallet-line" aria-hidden="true" />
            Sign up with Wallet (unavailable)
          </Button>
          <p className="text-sm text-[--text-dark-secondary]">
            You can add a passkey after creating your account.
          </p>
        </div>
        <Link
          to="/register"
          className="text-sm text-[--text-dark-secondary] hover:underline"
        >
          Back to role selection
        </Link>
        <DocumentModal
          isOpen={terms}
          onClose={() => setTerms(false)}
          title="Términos y Condiciones"
          content={TERMS_AND_CONDITIONS}
        />
      </div>
    );
  if (session && loadedFor !== session.principalId)
    return (
      <div>
        {error ? (
          <>
            <p role="alert">{error}</p>
            <Button onClick={() => setLoadAttempt((value) => value + 1)}>
              Retry onboarding
            </Button>
          </>
        ) : (
          <p role="status">Loading your saved profile…</p>
        )}
      </div>
    );
  if (step === 'success') return <SignupComplete />;
  return (
    <div className="onboardingContent [&_form>div]:!m-0 [&_form>div]:!space-y-0 [&_form]:flex [&_form]:flex-col [&_form]:gap-8 [&_form]:!space-y-0 [&_form_button]:!w-auto [&_form_div_div]:!space-y-0">
      {session && (
        <p className="mb-4 text-sm text-[--text-dark-secondary]">
          Your account is created. Finish your {workerRole.toLowerCase()} profile.{' '}
          <Link to="/dashboard">Continue later</Link>
        </p>
      )}
      {cached.current?.imageSelected && !draft.image && (
        <p role="status" className="text-sm text-[--text-dark-secondary]">
          Reselect your unsaved profile picture after reloading.
        </p>
      )}
      {step === 'personal-info' && (
        <PersonalInformationForm
          initialName={draft.profile.name}
          initialEmail={draft.profile.contactEmail ?? ''}
          initialGithubUsername={draft.profile.githubUsername ?? ''}
          initialPortfolioUrl={draft.profile.portfolioUrl ?? ''}
          initialImage={draft.image}
          onBack={(values) => {
            setDraft((previous) => ({
              ...previous,
              image: values.image,
              profile: {
                ...previous.profile,
                name: values.name,
                contactEmail: values.contactEmail,
                githubUsername: values.githubUsername || null,
                portfolioUrl: values.portfolioUrl || null,
              },
            }));
            if (!session) setStep('credentials');
            else window.history.back();
          }}
          onSubmit={(values) => void savePersonal(values)}
          isSubmitting={busy}
          errorMessage={error}
        />
      )}
      {step === 'about-yourself' && (
        <AboutYourselfForm
          initialBio={draft.profile.biography ?? ''}
          initialBackground={draft.profile.background ?? ''}
          initialProficiency={draft.profile.proficiency}
          initialRoleIds={draft.roleIds}
          initialSkillIds={draft.skillIds}
          onBack={(values) => {
            setDraft((previous) => ({
              ...previous,
              profile: {
                ...previous.profile,
                biography: values.bio,
                background: values.background,
                proficiency: values.proficiency,
                roleExperiences: values.roleIds.map((roleId) => ({
                  roleId,
                  proficiency: values.proficiency,
                })),
              },
              roleIds: values.roleIds,
              skillIds: values.skillIds,
            }));
            setStep('personal-info');
          }}
          onSubmit={(values) => void saveQualifications(values)}
          isSubmitting={busy}
          errorMessage={error}
        />
      )}
      {step === 'almost-there' && (
        <AlmostThereForm
          initialLocation={draft.profile.location ?? ''}
          initialLanguages={draft.profile.languages}
          initialAvailableHoursPerWeek={draft.hours}
          onBack={(values) => {
            setDraft((previous) => ({
              ...previous,
              profile: {
                ...previous.profile,
                location: values.location,
                languages: values.languages,
              },
              hours: values.availableHoursPerWeek ?? previous.hours,
            }));
            setStep('about-yourself');
          }}
          onSubmit={(values) => void finish(values)}
          isSubmitting={busy}
          pending={pending}
          statusMessage={status}
          errorMessage={error}
        />
      )}
    </div>
  );
}
